/**
 * 🏗️ ARQUITETURA DE 100K USUÁRIOS - Estado Global com Zustand
 * 
 * ✅ Fase 1: Estado Global (elimina re-renders do Context API)
 * 🔒 Segurança: Supabase já persiste sessão via httpOnly-like storage
 * 
 * 🎯 Benefícios:
 * - Componentes assinam apenas pedaços do estado (não o estado inteiro)
 * - Troca de perfil sem layout shift ou "piscadas"
 * - Sessão gerenciada de forma segura pelo Supabase client
 * - Estado desacoplado da árvore React
 * - Preparado para 100k usuários simultâneos
 * 
 * ✅ AUDIT FIX: Substituídos console.log por logger.debug
 */

import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';
import { usePreferencesStore } from './preferencesStore';
import { sessionModeStore } from './sessionModeStore';
import { logger } from '@/lib/logger';

export type ViewMode = 'teacher' | 'family' | 'admin' | 'student';

interface Profile {
  id: string;
  name: string;
  photo: string | null;
  date_of_birth: string | null;
}

interface UserRoles {
  isTeacher: boolean;
  isFamily: boolean;
  isAdmin: boolean;
  isStudent: boolean;
}

interface AuthState {
  // Estado
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  roles: UserRoles;
  activeMode: ViewMode | null;
  availableModes: ViewMode[];
  isLoading: boolean;
  isInitialized: boolean;

  // Ações
  setSession: (session: Session | null) => void;
  setProfile: (profile: Profile | null) => void;
  setRoles: (roles: UserRoles) => void;
  setActiveMode: (mode: ViewMode | null) => void;
  signOut: () => Promise<void>;
  initialize: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  // Estado inicial
  session: null,
  user: null,
  profile: null,
  roles: {
    isTeacher: false,
    isFamily: false,
    isAdmin: false,
    isStudent: false,
  },
  activeMode: null,
  availableModes: [],
  isLoading: true,
  isInitialized: false,

  // Setters
  setSession: (session) => {
    set({ session, user: session?.user ?? null });
  },

  setProfile: (profile) => {
    set({ profile });
  },

  setRoles: (roles) => {
    const availableModes: ViewMode[] = [];
    if (roles.isAdmin) availableModes.push('admin');
    if (roles.isTeacher) availableModes.push('teacher');
    if (roles.isFamily) availableModes.push('family');
    // Student mode is injected on-demand by studentAuthStore via setActiveMode('student')
    // Do NOT add it here — roles.isStudent means "family has linked students", not "user can use student mode"

    set({ roles, availableModes });

    // Prioridade de restauração de modo:
    // 1. sessionStorage (modo da sessão atual - sobrevive refresh)
    // 2. estado atual (se já existe)
    // 3. primeiro disponível
    const currentMode = get().activeMode;
    const sessionMode = sessionModeStore.get();

    if (sessionMode && availableModes.includes(sessionMode)) {
      logger.debug('[authStore] Restoring mode from sessionStorage:', sessionMode);
      set({ activeMode: sessionMode });
    } else if (!currentMode && availableModes.length > 0) {
      set({ activeMode: availableModes[0] });
    }
  },

  setActiveMode: (mode) => {
    // Handle null (logout from studentAuthStore)
    if (mode === null) {
      set({ activeMode: null });
      sessionModeStore.clear();
      return;
    }

    const { availableModes } = get();

    // Student mode comes from studentAuthStore, not from Supabase roles.
    // Accept it unconditionally and add to availableModes if missing.
    if (mode === 'student' && !availableModes.includes('student')) {
      const newModes: ViewMode[] = [...availableModes, 'student'];
      set({ activeMode: mode, availableModes: newModes });
      sessionModeStore.set(mode);
      logger.debug('[authStore] Student mode accepted (injected into availableModes)');
      return;
    }

    if (availableModes.includes(mode)) {
      set({ activeMode: mode });

      // Salvar modo atual em sessionStorage (sobrevive refresh na mesma aba)
      sessionModeStore.set(mode);

      // Persist preference em localStorage (exceto admin por segurança)
      if (mode !== 'admin') {
        usePreferencesStore.getState().setPreferredMode(mode);
      }

      logger.debug('[authStore] Profile switched to:', mode);
    } else {
      logger.warn('[authStore] Attempted to switch to unavailable mode:', mode);
    }
  },

  signOut: async () => {
    try {
      await supabase.auth.signOut();

      // Limpar sessionStorage (modo da aba)
      sessionModeStore.clear();

      // Limpar localStorage preference
      usePreferencesStore.getState().setPreferredMode(null);

      set({
        session: null,
        user: null,
        profile: null,
        roles: {
          isTeacher: false,
          isFamily: false,
          isAdmin: false,
          isStudent: false,
        },
        activeMode: null,
        availableModes: [],
      });
      logger.debug('[authStore] User signed out');
    } catch (error) {
      logger.error('[authStore] Sign out error:', error);
      throw error;
    }
  },

  initialize: async () => {
    logger.debug('[authStore] Initialize called');
    const timeoutId = setTimeout(() => {
      logger.error('[authStore] Initialization timeout (8s) - forcing completion');
      set({ isLoading: false, isInitialized: true });
    }, 8000);

    try {
      logger.debug('[authStore] Setting isLoading: true');
      set({ isLoading: true });

      logger.debug('[authStore] Fetching session...');
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        logger.error('[authStore] Session error:', sessionError);
        throw sessionError;
      }

      if (!session) {
        logger.debug('[authStore] No session found, clearing state');
        clearTimeout(timeoutId);
        set({ 
          isLoading: false, 
          isInitialized: true,
          session: null,
          user: null,
          profile: null,
        });
        return;
      }

      logger.debug('[authStore] Session found:', session.user.id);
      set({ session, user: session.user });

      logger.debug('[authStore] Fetching roles and profile...');
      // Fetch roles and profile in parallel - use maybeSingle to avoid throwing when profile doesn't exist
      const [rolesResult, profileResult] = await Promise.all([
        supabase.rpc('get_user_roles', { p_user_id: session.user.id }),
        supabase.from('profiles').select('id, name, photo, date_of_birth').eq('id', session.user.id).maybeSingle(),
      ]);

      // Tolerate partial errors - retry once for roles if first attempt fails
      let finalRolesResult = rolesResult;
      if (rolesResult.error) {
        logger.warn('[authStore] Roles fetch failed, retrying in 1s...', rolesResult.error);
        await new Promise((r) => setTimeout(r, 1000));
        finalRolesResult = await supabase.rpc('get_user_roles', { p_user_id: session.user.id });
        if (finalRolesResult.error) {
          logger.warn('[authStore] Roles retry also failed:', finalRolesResult.error);
        }
      }
      if (profileResult.error) {
        logger.warn('[authStore] Profile fetch failed:', profileResult.error);
      }

      const rolesData = finalRolesResult.data as { is_admin: boolean; is_teacher: boolean; is_family: boolean; is_student: boolean } | null;
      const roles: UserRoles = {
        isAdmin: rolesData?.is_admin ?? false,
        isTeacher: rolesData?.is_teacher ?? false,
        isFamily: rolesData?.is_family ?? false,
        isStudent: rolesData?.is_student ?? false,
      };

      logger.debug('[authStore] Roles loaded:', roles);
      get().setProfile(profileResult.data || null);
      get().setRoles(roles);

      logger.debug('[authStore] Initialized:', { roles, activeMode: get().activeMode });
      clearTimeout(timeoutId);

    } catch (error) {
      logger.error('[authStore] Initialization error:', error);
      if (error instanceof Error) {
        logger.error('[authStore] Error stack:', error.stack);
      }
      clearTimeout(timeoutId);
      
      // Keep session if exists - retry roles once before giving up
      const currentSession = get().session;
      if (currentSession) {
        logger.warn('[authStore] Session exists, retrying roles fetch...');
        try {
          await new Promise((r) => setTimeout(r, 1000));
          const retryRoles = await supabase.rpc('get_user_roles', { p_user_id: currentSession.user.id });
          if (!retryRoles.error && retryRoles.data) {
            const rd = retryRoles.data as { is_admin: boolean; is_teacher: boolean; is_family: boolean; is_student: boolean };
            const roles: UserRoles = {
              isAdmin: rd.is_admin ?? false,
              isTeacher: rd.is_teacher ?? false,
              isFamily: rd.is_family ?? false,
              isStudent: rd.is_student ?? false,
            };
            set({ session: currentSession, user: currentSession.user });
            get().setRoles(roles);
            logger.debug('[authStore] Retry succeeded, roles loaded');
            return;
          }
        } catch (retryErr) {
          logger.warn('[authStore] Retry also failed:', retryErr);
        }
      }
      
      // Check if studentAuthStore has an active session before defaulting to Visitante
      const studentStore = await import('./studentAuthStore').then(m => m.useStudentAuthStore.getState());
      const hasStudentSession = studentStore.isStudentLoggedIn;

      set({
        session: currentSession,
        user: currentSession?.user || null,
        profile: null,
        roles: {
          isTeacher: false,
          isFamily: false,
          isAdmin: false,
          isStudent: hasStudentSession,
        },
        activeMode: hasStudentSession ? 'student' : null,
        availableModes: hasStudentSession ? ['student'] : [],
      });
    } finally {
      logger.debug('[authStore] Setting isLoading: false, isInitialized: true');
      set({ isLoading: false, isInitialized: true });
    }
  },
}));

// Selector hooks para performance ótima
export const useSession = () => useAuthStore((state) => state.session);
export const useUser = () => useAuthStore((state) => state.user);
export const useProfile = () => useAuthStore((state) => state.profile);
export const useRoles = () => useAuthStore((state) => state.roles);
export const useActiveMode = () => useAuthStore((state) => state.activeMode);
export const useAvailableModes = () => useAuthStore((state) => state.availableModes);
export const useIsLoading = () => useAuthStore((state) => state.isLoading);

// Export types
export type { UserRoles, Profile };
